import React from 'react'
import { IValueEditorProps } from '@app-types/general'
import Input from '@components/forms/input'
import TextArea from '@components/forms/text_area'
import { INLINE_MAX_CHARS_THRESHOLD } from '@constants/text'
import { createLogger } from '@utils/logger'
import useValueEditor from '@hooks/useValueEditor'

// Initialize logger
const log = createLogger('StringEditor')
log.setLevel('info')

// Define interfaces for props and related data structures

type ChangeObject = {
  name?: string
  value: string
}

type StringEditorProps = Omit<IValueEditorProps, 'serializeValue' | 'parseValue'> & {
  serializeValue?: (value: string | null) => string | null
  parseValue?: (value: string | null) => string | null
}

const passthrough = (value: string | null): string | null => value
const defaultParseValueFn = passthrough
const defaultSerializeValueFn = passthrough

const StringEditor: React.FC<StringEditorProps> & { EDITOR_TYPE: string } = ({
  name,
  value = '',
  onChange,
  parseValue = defaultParseValueFn,
  serializeValue = defaultSerializeValueFn,
  editorOptions = {},
  onEditExit,
  ...restProps
}) => {
  // No internal save logic; parent controls commit.

  const { localValue, handleLocalChange } = useValueEditor<string | null, string | null>({
    value,
    parseValue,
    serializeValue,
    onChange: onChange ? ({ value }) => onChange({ name, value }) : undefined,
  })

  // External blur + immediate done signal
  const handleBlur = (e?: any) => {
    onEditExit?.()
    ;(restProps as any)?.onBlur?.(e)
  }

  // trigger blur event when enter key is pressed, which in turn will call our handleBlur
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      ;(event.target as HTMLInputElement).blur() // Trigger onBlur callback
    }
  }

  const handleChange = (changeObj: ChangeObject) => {
    handleLocalChange(changeObj.value)
  }

  const normalizedValue = localValue ?? ''

  // Decide whether to use TextArea (multi-line) or Input (single-line)
  const maxTextLength = editorOptions?.maxTextLength
  const recommendedDisplay = editorOptions?.recommendedDisplay as 'inline' | 'popover' | undefined
  const textSize = editorOptions?.textSize as 'short' | 'medium' | undefined
  const shouldUseTextArea =
    (typeof maxTextLength === 'number' && maxTextLength > INLINE_MAX_CHARS_THRESHOLD) ||
    textSize === 'medium' ||
    recommendedDisplay === 'popover'

  // Note: If we decide to enforce max length at Input level in the future, we can do it here
  return (
    <span className="content-editor content-editor--string string-editor">
      {shouldUseTextArea ? (
        <TextArea
          {...restProps}
          name={name}
          value={normalizedValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className="input"
          autoSize={true}
        />
      ) : (
        <Input
          {...restProps}
          name={name}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          value={normalizedValue}
        />
      )}
    </span>
  )
}
StringEditor.displayName = 'StringEditor'
StringEditor.EDITOR_TYPE = 'string'

export default StringEditor
